import { Inbox, Layers, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { BadgeProps } from '../Badge';
import type { ToastHistoryItem, ToastHistoryType } from './toastHistoryStore';

import { Badge } from '../Badge';
import { Button } from '../Button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../Sheet';
import { Typography } from '../Typography';
import { clearToastHistory, readToastHistory, subscribeToToastHistory } from './toastHistoryStore';

const variantByType: Record<ToastHistoryType, BadgeProps['variant']> = {
  default: 'outline',
  message: 'outline',
  info: 'secondary',
  loading: 'secondary',
  success: 'success',
  warning: 'warning',
  error: 'destructive',
};

const labelByType: Record<ToastHistoryType, string> = {
  default: 'Default',
  message: 'Message',
  info: 'Info',
  loading: 'Loading',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
};

const useToastHistory = () => {
  const [items, setItems] = useState<ToastHistoryItem[]>(() => readToastHistory());

  useEffect(() => {
    return subscribeToToastHistory(() => setItems(readToastHistory()));
  }, []);

  return items;
};

const formatToastTime = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const ToastHistoryEmptyState = () => (
  <div className="flex min-h-72 flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-8 text-center">
    <div className="bg-muted text-muted-foreground rounded-full p-3">
      <Inbox className="h-5 w-5" />
    </div>
    <div className="space-y-1">
      <Typography variant="body" className="font-medium">
        No toasts yet
      </Typography>
      <Typography variant="bodySmall" className="text-muted-foreground max-w-64">
        Recent toast messages will appear here after they are shown.
      </Typography>
    </div>
  </div>
);

const ToastHistoryRow = ({ item }: { item: ToastHistoryItem }) => (
  <div className="bg-card text-card-foreground rounded-lg border p-3 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={variantByType[item.type]}>{labelByType[item.type]}</Badge>
          {item.status === 'dismissed' && <Badge variant="outline">Dismissed</Badge>}
          <span className="text-muted-foreground text-xs">{formatToastTime(item.updatedAt)}</span>
        </div>
        <div className="space-y-1">
          <Typography variant="body" className="break-words font-medium leading-5">
            {item.title}
          </Typography>
          {item.description && (
            <Typography variant="bodySmall" className="text-muted-foreground break-words">
              {item.description}
            </Typography>
          )}
        </div>
      </div>
    </div>
    {item.route && <div className="text-muted-foreground mt-3 truncate text-xs">{item.route}</div>}
  </div>
);

export const ToastHistoryButton = () => {
  const items = useToastHistory();
  const hasItems = items.length > 0;
  const errorCount = useMemo(() => items.filter((item) => item.type === 'error').length, [items]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" onlyIcon className="ml-auto h-8 w-8" aria-label="Open toast history">
          <Layers className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        title="Toast history"
        description="Recent toast messages"
        className="w-full p-0 sm:max-w-xl"
      >
        <div className="flex h-full flex-col">
          <SheetHeader className="border-b p-6 pr-12">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <SheetTitle>Toast history</SheetTitle>
                <SheetDescription>
                  Last {items.length} stored toasts{errorCount > 0 ? ` · ${errorCount} errors` : ''}
                </SheetDescription>
              </div>
              <Button variant="outline" size="sm" disabled={!hasItems} onClick={clearToastHistory}>
                <Trash2 className="h-4 w-4" />
                Clear
              </Button>
            </div>
          </SheetHeader>
          <div className="space-y-3 p-4">
            {hasItems ? items.map((item) => <ToastHistoryRow key={item.id} item={item} />) : <ToastHistoryEmptyState />}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
